import { Injectable } from "@angular/core";
import { environment } from "../../../core/config/environment";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { catchError, firstValueFrom, Observable, throwError } from "rxjs";
import { VendorViewDto } from "../../../personas/vendor/models/vendorDto";
import { LoggerService } from "../../../shared/services/logger.service";
import { db, JobIssueVendor, Lot } from "../../../core/database/db";
import { VendorDTO } from "../models/VendorDTO";
import { TitleCasePipe } from "@angular/common";

@Injectable({ providedIn: "root" })
export class VendorService {

  private readonly vendorsByFinancialCommunityUrlBase = `${environment.backend.apiBaseURL}/api/Job/GetVendorByFinancialCommunityId`;

  private readonly url = `${environment.backend.apiBaseURL}/api/vendor/validatetoken/`;

  private readonly titleCasePipe = new TitleCasePipe();

  constructor(
    private http: HttpClient,
    private logger: LoggerService,
  ) { }

  getValidationPayload(token: string): Observable<VendorViewDto> {
    const url = `${this.url}${token}`;
    return this.http.get<VendorViewDto>(url).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 400) {
          return throwError(() => new Error("Vendor token is expired or does not exists. (400)"));
        }
        this.logger.error(err);
        return throwError(() => err);
      })
    );
  }

  async getSentJobIssueVendorsByIssueIdentifiers(issueIdentifiers: string[]): Promise<JobIssueVendor[]> {
    return await db.jobIssueVendor
      .where("issueIdentifier")
      .anyOf(issueIdentifiers)
      .and(issueVendor => issueVendor.isSent)
      .toArray();
  }

  async getVendorsForJob(jobId: number): Promise<VendorDTO[]> {
    try {
      const job = await db.inspectionJob.get(jobId);

      if (!job) {
        this.logger.warn(`Job with ID ${jobId} not found`);
        return [];
      }

      return this.getVendorsForLotId(job.lotId);
    } catch (error) {
      this.logger.error(`Error fetching vendors for job ${jobId}`, error);
      return [];
    }
  }

  async getVendorsForLotId(lotId: number): Promise<VendorDTO[]> {
    const lot = await db.lot.get(lotId);

    if (!lot) {
      this.logger.warn(`Lot with ID ${lotId} not found`);
      return [];
    }

    return this.getVendorsForLots([lot]);
  }

  async getVendorsForLotIds(lotIds: number[]): Promise<VendorDTO[] | null> {
    const lots = await db.lot
      .where("id").anyOf(lotIds)
      .toArray();

    if (lots.length === 0) {
      this.logger.warn(`Lots with IDs ${lotIds.join(", ")} not found`);
      return null;
    }

    return this.getVendorsForLots(lots);
  }

  async getVendorsForLots(lots: Lot[]) {
    const uniqueExternalFinancialCommunityIds = lots
      .reduce((acc, j) => {
        const key = `${j.externalFinancialCommunityId}-${j.externalDivisionId}`;
        if (!acc.has(key)) {
          acc.set(key, {
            externalFinancialCommunityId: j.externalFinancialCommunityId,
            jobDivisionId: j.externalDivisionId
          });
        }
        return acc;
      }, new Map<string, { externalFinancialCommunityId: number; jobDivisionId: number }>());

    const results = await Promise.all(Array.from(uniqueExternalFinancialCommunityIds.values()).map(async jobData => {
      const url = `${this.vendorsByFinancialCommunityUrlBase}/${jobData.jobDivisionId}/${jobData.externalFinancialCommunityId}`;
      return await firstValueFrom(this.http.get<VendorDTO[]>(url));
    }));

    const allVendors = results.reduce(((prev, curr) => [...prev, ...curr]));

    return allVendors;
  }

  async getVendorsForAllLots(): Promise<VendorDTO[]> {
    const allLots = await db.lot.toArray();
    return this.getVendorsForLots(allLots);
  }

  public processVendorData(vendors: VendorDTO[]): VendorDTO[] {
    const uniqueVendors = new Map<number, VendorDTO>();

    vendors.forEach(vendor => {
      if (!uniqueVendors.has(vendor.id)) {
        uniqueVendors.set(vendor.id, {
          ...vendor,
          name: this.titleCasePipe.transform(vendor.name),
          doingBusinessAs: this.titleCasePipe.transform(vendor.doingBusinessAs),
        });
      }
    });

    return Array.from(uniqueVendors.values()).sort((a, b) => {
      const left = (a.doingBusinessAs || a.name || "").toLowerCase();
      const right = (b.doingBusinessAs || b.name || "").toLowerCase();
      return left.localeCompare(right);
    });
  }

}
